import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wrench, Clock, Mail, Phone, RefreshCw, Lock } from 'lucide-react';
import { useMaintenanceMode } from '../hooks/useMaintenanceMode';

const MaintenancePage = () => {
  const { maintenanceMode, refresh } = useMaintenanceMode();
  const history = useHistory();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(30);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Auto-refresh countdown and check
  useEffect(() => {
    const countdown = setInterval(() => {
      setAutoRefreshCountdown((prev) => {
        if (prev <= 1) {
          refresh(); // Check maintenance status
          return 30; // Reset countdown
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [refresh]);

  const handleManualRefresh = () => {
    setAutoRefreshCountdown(30);
    refresh();
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Australia/Perth'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Australia/Perth'
    });
  };

  const getEstimatedReturn = () => {
    if (maintenanceMode?.scheduled_end) {
      const endTime = new Date(maintenanceMode.scheduled_end);
      return endTime.toLocaleString('en-AU', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Australia/Perth'
      });
    }
    return null;
  };

  return (
    <div className="h-screen flex flex-col bg-brand-dark overflow-hidden">
      {/* Main Content */}
      <div className="flex-grow flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8 overflow-y-auto">
        <div className="max-w-2xl w-full space-y-8">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <img
              src="/Logo2.png"
              alt="Company Logo"
              className="mx-auto h-24 md:h-36 w-auto"
            />
          </motion.div>

          {/* Maintenance Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-[#242222] rounded-lg shadow-2xl border border-brand/20 p-8 sm:p-12"
          >
            {/* Animated Icon */}
            <div className="flex justify-center mb-6">
              <motion.div
                animate={{
                  rotate: [0, -10, 10, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 1,
                }}
                className="bg-brand rounded-full p-6"
              >
                <Wrench className="h-16 w-16 text-brand-dark" />
              </motion.div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-extrabold text-brand text-center mb-4">
              Under Maintenance
            </h1>

            {/* Message */}
            <div className="bg-brand-dark rounded-lg p-6 mb-6">
              <p className="text-gray-300 text-center text-lg leading-relaxed">
                {maintenanceMode?.message || 
                  "We are currently performing scheduled maintenance to improve your experience. We'll be back shortly. Thank you for your patience!"}
              </p>
            </div>

            {/* Estimated Return Time */}
            {getEstimatedReturn() && (
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center text-blue-300">
                  <Clock className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    Expected to return: <strong>{getEstimatedReturn()}</strong>
                  </span>
                </div>
              </div>
            )}

            {/* Current Time */}
            <div className="text-center mb-6">
              <p className="text-gray-400 text-sm mb-1">{formatDate(currentTime)}</p>
              <p className="text-brand text-2xl font-mono font-bold">
                {formatTime(currentTime)}
              </p>
              <p className="text-gray-500 text-xs mt-1">AWST (Perth Time)</p>
            </div>

            {/* Auto-refresh Info */}
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="flex items-center text-gray-400 text-sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                <span>Auto-checking in {autoRefreshCountdown}s</span>
              </div>
              <button
                onClick={handleManualRefresh}
                className="text-brand hover:text-brand-hover text-sm font-medium underline focus:outline-none"
              >
                Check Now
              </button>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-700 my-6"></div>

            {/* Contact Information */}
            <div className="space-y-4">
              <p className="text-gray-400 text-center text-sm font-medium mb-3">
                Need urgent assistance?
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Email */}
                <a
                  href="mailto:matthew@mhs.com.au"
                  className="flex items-center justify-center space-x-2 bg-brand-dark hover:bg-[#2a2828] rounded-lg p-4 transition-colors duration-200 border border-gray-700 hover:border-brand/50"
                >
                  <Mail className="h-5 w-5 text-brand" />
                  <span className="text-gray-300 text-sm">matthew@mhs.com.au</span>
                </a>

                {/* Phone */}
                <a
                  href="tel:0433175581"
                  className="flex items-center justify-center space-x-2 bg-brand-dark hover:bg-[#2a2828] rounded-lg p-4 transition-colors duration-200 border border-gray-700 hover:border-brand/50"
                >
                  <Phone className="h-5 w-5 text-brand" />
                  <span className="text-gray-300 text-sm">0433 175 581</span>
                </a>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-xs">
                Business Hours: Monday - Friday, 7:30 AM - 5:00 PM AWST
              </p>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center"
          >
            <p className="text-gray-500 text-sm">
              This page will automatically refresh when maintenance is complete.
            </p>

            {/* Admin Login - allows staff/admins to sign in and manage maintenance */}
            <button
              onClick={() => history.push('/login')}
              className="mt-4 inline-flex items-center text-gray-500 hover:text-brand text-sm font-medium transition-colors duration-200 focus:outline-none focus:underline"
            >
              <Lock className="h-4 w-4 mr-1.5" />
              Admin Login
            </button>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#242222] border-t border-brand/20 py-3 flex-shrink-0">
        <div className="px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
            {/* Left: Company Info */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <img src="/Logo2.png" alt="MHS Logo" className="h-6 w-auto mr-2" />
                <span className="font-semibold text-gray-300">MHS</span>
              </div>
            </div>

            {/* Right: STAUFF Logo */}
            <div className="flex items-center space-x-4">
              <img 
                src="/logo-stauff.png" 
                alt="STAUFF" 
                className="h-6 w-auto opacity-75"
              />
              <div className="hidden sm:block h-4 w-px bg-gray-600"></div>
              <p className="text-xs text-gray-500">
                Partner Portal - Beta v0.9.5
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MaintenancePage;
